import { Injectable } from '@angular/core';
import { AuthService } from '../../core/services/authentication.service';
import { MessagingService } from './messages.services';
import { NotificationsClientService } from './notifications-client.service';

@Injectable()
export class NotificationHealthService {

    private started = false;
    private wakeUpIntervalId: any = null;
    private heartbeatIntervalId: any = null;
    private pendingRecoveryTimeoutId: any = null;
    private pendingRecoveryReason: string | null = null;
    private pendingRecoveryRunAt = 0;
    private lastTickAt = 0;
    private lastHiddenAt = 0;
    private recoveryInFlight = false;
    private lastRecoveryAt = 0;
    private isOffline = false;
    private pendingOnlineRecoveryReason: string | null = null;

    private readonly wakeUpTickMs = 30000;
    private readonly wakeUpGapThresholdMs = 65000;
    private readonly heartbeatMs = 5 * 60 * 1000;
    private readonly staleRegistrationMs = 6 * 60 * 60 * 1000;
    private readonly visibilityHiddenMinMs = 60000;
    private readonly recoveryCooldownMs = 30000;
    private readonly resumeRecoveryDelayMs = 5000;
    private readonly recoveryInFlightRetryMs = 5000;

    private readonly onlineHandler = () => this.handleOnline();
    private readonly offlineHandler = () => this.handleOffline();
    private readonly visibilityHandler = () => this.handleVisibilityChange();

    constructor(
        private authService: AuthService,
        private messagingService: MessagingService,
        private notificationsClient: NotificationsClientService
    ) { }

    public start(): void {
        if (this.started) return;
        if (typeof window === 'undefined') return;

        this.started = true;
        this.lastTickAt = Date.now();
        this.isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

        window.addEventListener('online', this.onlineHandler);
        window.addEventListener('offline', this.offlineHandler);
        if (typeof document !== 'undefined' && document.addEventListener) {
            document.addEventListener('visibilitychange', this.visibilityHandler);
        }

        this.wakeUpIntervalId = setInterval(() => this.wakeUpTick(), this.wakeUpTickMs);
        this.heartbeatIntervalId = setInterval(() => this.heartbeat(), this.heartbeatMs);

    }

    public stop(): void {
        if (!this.started) return;

        if (this.wakeUpIntervalId) {
            clearInterval(this.wakeUpIntervalId);
            this.wakeUpIntervalId = null;
        }
        if (this.heartbeatIntervalId) {
            clearInterval(this.heartbeatIntervalId);
            this.heartbeatIntervalId = null;
        }
        if (this.pendingRecoveryTimeoutId) {
            clearTimeout(this.pendingRecoveryTimeoutId);
            this.pendingRecoveryTimeoutId = null;
        }

        if (typeof window !== 'undefined') {
            window.removeEventListener('online', this.onlineHandler);
            window.removeEventListener('offline', this.offlineHandler);
        }
        if (typeof document !== 'undefined' && document.removeEventListener) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }

        this.started = false;
        this.lastTickAt = 0;
        this.lastHiddenAt = 0;
        this.recoveryInFlight = false;
        this.lastRecoveryAt = 0;
        this.pendingOnlineRecoveryReason = null;
        this.pendingRecoveryReason = null;
        this.pendingRecoveryRunAt = 0;

    }

    public async triggerRecovery(reason: string): Promise<void> {
        const now = Date.now();

        if (this.isOffline) {
            this.pendingOnlineRecoveryReason = reason;
            return;
        }

        if (this.recoveryInFlight) {
            this.scheduleRecovery(reason, this.recoveryInFlightRetryMs);
            return;
        }

        if (now - this.lastRecoveryAt < this.recoveryCooldownMs) {
            const delayMs = this.recoveryCooldownMs - (now - this.lastRecoveryAt);
            this.scheduleRecovery(reason, delayMs);
            return;
        }

        if (!this.authService || !this.authService.sessionActive()) {
            return;
        }

        this.recoveryInFlight = true;
        this.lastRecoveryAt = now;

        try {
            const hasKnownToken = !!this.messagingService.getCurrentFcmToken();
            if (hasKnownToken && this.shouldRecoverPushDeliveryChannel(reason)) {
                await this.messagingService.recoverPushDeliveryChannel(reason);
            } else {
                await this.messagingService.revalidateFcmToken(reason);
            }
        } catch (error) {
            console.warn('[Health] recovery failed', { reason, error });
        } finally {
            this.recoveryInFlight = false;
        }
    }

    private wakeUpTick(): void {
        const now = Date.now();
        const gap = now - this.lastTickAt;
        this.lastTickAt = now;

        if (gap > this.wakeUpGapThresholdMs) {
            this.refreshNotificationsIfOnline();
            this.scheduleRecovery('wake-up', this.resumeRecoveryDelayMs);
        }
    }

    private handleOnline(): void {
        this.isOffline = false;
        this.refreshNotificationsIfOnline();
        const reason = this.pendingOnlineRecoveryReason || 'online';
        this.pendingOnlineRecoveryReason = null;
        this.scheduleRecovery(reason, this.resumeRecoveryDelayMs);
    }

    private handleOffline(): void {
        this.isOffline = true;
    }

    private handleVisibilityChange(): void {
        if (typeof document === 'undefined') return;

        if (document.visibilityState === 'visible') {
            const hiddenFor = this.lastHiddenAt > 0 ? Date.now() - this.lastHiddenAt : 0;
            if (hiddenFor > this.visibilityHiddenMinMs) {
                this.refreshNotificationsIfOnline();
                this.messagingService.recoverPushDeliveryChannel('visibility');
            }
            this.lastHiddenAt = 0;
        } else if (document.visibilityState === 'hidden') {
            this.lastHiddenAt = Date.now();
        }
    }

    private async heartbeat(): Promise<void> {
        if (!this.authService || !this.authService.sessionActive()) {
            return;
        }

        const hasToken = !!this.messagingService.getCurrentFcmToken();
        const isOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
        const lastRegisteredAt = this.messagingService.getLastFcmSubscriptionRegisteredAt();
        const stale = lastRegisteredAt > 0 && (Date.now() - lastRegisteredAt) > this.staleRegistrationMs;

        if (!isOnline) return;

        if (!hasToken) {
            this.triggerRecovery('heartbeat-no-token');
            return;
        }

        const swAlive = await this.isServiceWorkerAlive();
        if (!swAlive) {
            this.refreshNotificationsIfOnline();
            this.triggerRecovery('heartbeat-sw-dead');
            return;
        }

        if (stale) {
            this.triggerRecovery('heartbeat-stale');
            return;
        }
    }

    private async isServiceWorkerAlive(): Promise<boolean> {
        if (typeof navigator === 'undefined' ||
            !navigator.serviceWorker ||
            typeof navigator.serviceWorker.getRegistrations !== 'function') {
            return false;
        }

        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            if (!registrations || registrations.length === 0) return false;

            const messagingRegistration = registrations.find((registration) => {
                const worker = registration.active || registration.waiting || registration.installing;
                const scriptUrl = worker && worker.scriptURL ? worker.scriptURL : '';
                return scriptUrl.indexOf('firebase-messaging-sw.js') !== -1 ||
                    (registration.scope || '').indexOf('firebase-cloud-messaging-push-scope') !== -1;
            });

            if (!messagingRegistration) return false;

            return !!messagingRegistration.active;
        } catch (error) {
            return false;
        }
    }

    private shouldRecoverPushDeliveryChannel(reason: string): boolean {
        return reason === 'wake-up' || reason === 'online' || reason === 'heartbeat-sw-dead';
    }

    private refreshNotificationsIfOnline(): void {
        const online = !this.isOffline &&
            (typeof navigator === 'undefined' || navigator.onLine !== false);

        if (!online) return;

        this.notificationsClient.requestNotificationsRefresh();
    }

    private scheduleRecovery(reason: string, delayMs: number): void {
        if (this.isOffline) {
            this.pendingOnlineRecoveryReason = reason;
            return;
        }

        const normalizedDelayMs = Math.max(0, delayMs);
        const runAt = Date.now() + normalizedDelayMs;
        this.pendingRecoveryReason = reason;

        if (this.pendingRecoveryTimeoutId && this.pendingRecoveryRunAt <= runAt) {
            return;
        }

        if (this.pendingRecoveryTimeoutId) {
            clearTimeout(this.pendingRecoveryTimeoutId);
        }

        this.pendingRecoveryRunAt = runAt;
        this.pendingRecoveryTimeoutId = setTimeout(() => {
            const pendingReason = this.pendingRecoveryReason || reason;
            this.pendingRecoveryTimeoutId = null;
            this.pendingRecoveryReason = null;
            this.pendingRecoveryRunAt = 0;
            this.triggerRecovery(pendingReason);
        }, normalizedDelayMs);
    }
}
